export interface MosaicInput {
    /** Quantas telas estao no mosaico. */
    count: number
    /** Espaco util do palco, em px. */
    width: number
    height: number
    /** Proporcao de cada tile (16/9 por padrao). */
    aspect?: number
    /** Espaco entre tiles, em px. */
    gap?: number
}

export interface MosaicLayout {
    cols: number
    rows: number
    tileWidth: number
    tileHeight: number
}

const EMPTY: MosaicLayout = { cols: 0, rows: 0, tileWidth: 0, tileHeight: 0 }

/**
 * Escolhe a grade que faz cada tela ocupar o MAIOR espaco possivel.
 *
 * Testa todas as combinacoes de colunas (1..count), calcula quanto cada tile
 * mediria mantendo a proporcao, e fica com a de maior area. Com uma tela so,
 * o resultado natural e ela sozinha preenchendo o palco inteiro — que e
 * exatamente o comportamento pedido pro modo "uma tela grande".
 */
export function computeMosaicLayout({
    count,
    width,
    height,
    aspect = 16 / 9,
    gap = 12
}: MosaicInput): MosaicLayout {
    if (count <= 0 || width <= 0 || height <= 0 || aspect <= 0) return EMPTY

    let best: MosaicLayout = EMPTY
    let bestArea = 0

    for (let cols = 1; cols <= count; cols++) {
        const rows = Math.ceil(count / cols)
        const cellWidth = (width - gap * (cols - 1)) / cols
        const cellHeight = (height - gap * (rows - 1)) / rows
        if (cellWidth <= 0 || cellHeight <= 0) continue

        /**
         * O tile e o maior retangulo na proporcao pedida que cabe na celula.
         *
         * Arredondado PRA BAIXO de proposito. `cols * tileWidth + gap` batia
         * exatamente na largura do palco, e "exatamente" e o pior caso pra uma
         * linha de flex: o `clientWidth` que mede o palco e inteiro, mas o
         * content box real e fracionario, entao sobrava menos espaco do que a
         * conta previa e o ultimo tile quebrava pra linha de baixo. Duas telas
         * que deviam ficar lado a lado viravam uma coluna com o dobro da
         * altura, passando da margem. Um pixel por coluna de folga resolve sem
         * que ninguem enxergue a diferenca.
         */
        const tileWidth = Math.floor(Math.min(cellWidth, cellHeight * aspect))
        // A altura segue a largura SEM arredondar: e o que mantem a proporcao
        // exata (arredondar os dois lados distorce o quadro).
        const tileHeight = tileWidth / aspect
        const area = tileWidth * tileHeight

        // Empate resolve pela grade mais "cheia" (menos buracos na ultima linha).
        if (area > bestArea + 0.5) {
            bestArea = area
            best = { cols, rows, tileWidth, tileHeight }
        }
    }

    return best
}

/** Proporcao media das telas visiveis — evita tarja preta quando todas sao ultrawide. */
export function averageAspect(aspects: number[], fallback = 16 / 9): number {
    const valid = aspects.filter((value) => Number.isFinite(value) && value > 0)
    if (valid.length === 0) return fallback
    return valid.reduce((sum, value) => sum + value, 0) / valid.length
}
